import { Box, Flex } from '@chakra-ui/react';
import { useState } from 'react';

import {
  SankeyChart,
  type SankeyData,
  type Link as SankeyLink,
  type Node as SankeyNode,
} from '@revelio/replots';

import { usePlotMetric } from '../../../hooks/usePlotMetric';
import type { BreakdownSingleDataSet } from '../../DataProvider/types';
import { PlotFilterType } from '../../PageConfigProvider/types/item';
import { usePageState } from '../../PageState/PageStateProvider';
import { useChart } from '../ChartProvider';
import { SharableChart } from '../chart-menu/plot-share/SharableChart';
import { SankeyFooter } from './SankeyFooter';

export const Sankey = ({ data }: { data: BreakdownSingleDataSet['data'] }) => {
  const metric = usePlotMetric();

  const { item } = useChart();
  const { pageState } = usePageState();
  const selectedEntityName = pageState.primaryEntities?.[item?.id]?.name;

  const color = item?.plot?.color;
  const showNodeLabels = item?.plot?.showDataLabels;

  const topNFilterValue = item?.plot?.plotFilters?.find(
    (f) => f.type === PlotFilterType.TOP_N
  )?.value;
  const isFixedCount =
    typeof topNFilterValue === 'number' && topNFilterValue > 0;

  const [numOfNodes, setNumOfNodes] = useState(10);
  const effectiveNumOfNodes = isFixedCount ? topNFilterValue : numOfNodes;

  const selectedEntityData =
    data?.find((d) => d.dim === selectedEntityName) ?? data?.[0];

  const dataBreakdown = selectedEntityData?.breakdown?.slice(
    0,
    effectiveNumOfNodes
  );

  const sankeyData = ((): SankeyData | null => {
    if (!dataBreakdown) return null;

    /**
     * Assign unique IDs to nodes so that duplicate labels (e.g., "London") are
     * treated as separate nodes in the Sankey diagram. We append an index
     * suffix when a label appears more than once ("London (2)", "London (3)").
     */
    const counts = new Map<string, number>();
    const getUniqueId = (label: string): string => {
      const count = counts.get(label) ?? 0;
      counts.set(label, count + 1);
      return count === 0 ? label : `${label} (${count + 1})`;
    };

    // Primary node (always unique as first occurrence)
    const primaryLabel = selectedEntityData?.dim ?? '';
    const primaryId = getUniqueId(primaryLabel);
    const primaryNode: SankeyNode = { id: primaryId };

    // Flow nodes with unique IDs
    const flowNodes: { original: string; node: SankeyNode }[] =
      dataBreakdown.map(({ dim }) => {
        const uniqueId = getUniqueId(dim);
        return { original: dim, node: { id: uniqueId } };
      });

    const nodes: SankeyNode[] = [primaryNode, ...flowNodes.map((f) => f.node)];

    // Build links referencing unique IDs
    const links: SankeyLink[] = dataBreakdown
      .map(({ value, share }, index) => {
        const flowNodeId = flowNodes[index]?.node?.id;
        if (!flowNodeId) return null;

        const sourceId =
          metric === 'transitions_inflow' ? flowNodeId : primaryId;
        const targetId =
          metric === 'transitions_inflow' ? primaryId : flowNodeId;
        return {
          source: sourceId,
          target: targetId,
          value: value ?? 0,
          share: share ?? 0,
        };
      })
      .filter((link) => link !== null);

    return { nodes, links };
  })();

  return (
    sankeyData && (
      <Flex
        position="relative"
        height="100%"
        flexDirection="column"
        paddingX="1rem"
      >
        <Box
          height={isFixedCount ? '100%' : 'calc(100% - 32px)'}
          position="relative"
        >
          <SharableChart
            Component={SankeyChart}
            data={sankeyData}
            isOutflows={metric === 'transitions_outflow'}
            color={color}
            showNodeLabels={showNodeLabels}
          />
        </Box>
        {!isFixedCount ? (
          <SankeyFooter
            numOfNodes={numOfNodes}
            onNumOfNodesChange={setNumOfNodes}
          />
        ) : null}
      </Flex>
    )
  );
};
